import * as React from 'react';
import { UserContext } from './user';
import { User } from '@lib/user';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { ME_QUERY, LOGOUT_MUTATION } from '@/gql/user';
import { MeQuery } from '@/gql/__generated__/graphql';

export interface UseProviderProps {
  children: React.ReactNode;
}

function parseUser(me: MeQuery['me']): User | null {
  if (!me) {
    return null;
  }

  return {
    id: me.id,
    email: me.email || '',
    lastName: me.lastName || null,
    name: me.name || null,
    documentNumber: me.identificationDocument?.id || null,
    phone: me.phoneNumber || null,
    passwordSet: me.passwordSet,
    profileUpdated: me.profileUpdated,
    role: me.role.id,
    verified: me.emailVerified,
    termsAccepted: me.termsAccepted,
  };
}

export const UserProvider: React.FC<UseProviderProps> = (props) => {
  const client = useApolloClient();
  const [logout] = useMutation(LOGOUT_MUTATION);
  const { loading, data, refetch } = useQuery(ME_QUERY);

  const user = React.useMemo(() => (loading ? false : parseUser(data?.me)), [loading, data]);

  return (
    <UserContext.Provider
      value={React.useMemo(
        () => ({
          user: user || null,
          loading: user === false,

          refresh() {
            return refetch();
          },

          async logout() {
            try {
              await logout({
                errorPolicy: 'ignore',
                update(cache) {
                  cache.updateQuery({ query: ME_QUERY }, (data) => ({
                    ...data,
                    me: null,
                  }));
                },
              });
            } finally {
              await client.clearStore();
            }
          },
        }),
        [user, loading, refetch, client, logout],
      )}
    >
      {props.children}
    </UserContext.Provider>
  );
};
